import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { stackAPI, userAPI, v2StackAPI, twistedStackAPI } from '../services/api';
import { getDiceBearGroupAvatar } from '../services/dicebear';
import { getDiceBearUserAvatar } from '../services/dicebear';
import { getStackDisplayName, getBotStackDisplayName } from '../utils/stack';
import { TEXT_COLORS } from '../utils/textColors';
import { getStackFilters, updateStackFilter } from '../utils/localStorage';
import { BUTTON_COLORS } from '../utils/buttonColors';
import { confirmAction, acknowledgeAction } from '../utils/confirmationDialog';
import CreateStack from './CreateStack';
import BotStackSelector from './BotStackSelector';

const helperLinkStyle = { color: 'var(--color-primary)', cursor: 'pointer', textDecoration: 'underline', background: 'transparent', border: 'none', padding: 0, font: 'inherit' };

const Stacks = ({ onClose, onStackClick, isEmbedded = false, currentUser: currentUserProp = null }) => {
  const [friends, setFriends] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createStackDialog, setCreateStackDialog] = useState({ isOpen: false, mode: null, targetId: null, targetName: null, targetAvatar: null, title: null, editingStack: null, isDraftEdit: false, isCollabEdit: false });
  const [botStackSelector, setBotStackSelector] = useState({ isOpen: false, stackId: null });
  const [actionsExpanded, setActionsExpanded] = useState(false);

  // Helper method to get banner info for a stack
  const getBannerInfo = useCallback((stack, meEntry) => {
    const isTwisted = Boolean(stack?.twist);
    const isDraft = stack?.status === 'draft';
    const isCollab = Boolean(stack?.is_collab);
    const unviewedUsers = (stack.users || []).filter(u => {
      const answeredGate = isTwisted ? Boolean(u.is_answered) : true;
      return answeredGate && !u.is_viewed;
    });
    const unviewedUsernames = unviewedUsers.map(u => u.user.username);
    const hasAnyUnviewed = unviewedUsernames.length > 0;

    // Check for unviewed comments (lowest priority) - use the new me field structure
    const hasUnviewedComments = Boolean(meEntry && meEntry.viewed_comments === false);

    if (isTwisted) {
      // Show yellow banner only if the viewer hasn't answered AND hasn't viewed the twisted stack yet
      const showNewTwisted = Boolean(meEntry && !meEntry.is_answered && !meEntry.viewed);
      if (showNewTwisted) {
        return {
          type: 'new-twisted',
          color: TEXT_COLORS.BANNER_NEW,
          text: 'New twisted stack!'
        };
      }
      if (hasAnyUnviewed) {
        return {
          type: 'new-responses',
          color: TEXT_COLORS.BANNER_RESPONSES,
          text: `New response${unviewedUsernames.length > 1 ? 's' : ''} from ${unviewedUsernames.join(', ')}`
        };
      }
      if (hasUnviewedComments) {
        return {
          type: 'new-comments',
          color: TEXT_COLORS.BANNER_COMMENTS,
          text: 'New comments!'
        };
      }
      return null;
    }

    // Check for collaborative draft state (after twisted stacks, before regular draft)
    if (isCollab && isDraft) {
      return {
        type: 'collab',
        color: TEXT_COLORS.BANNER_COLLAB,
        text: 'New collab stack!'
      };
    }

    // Check for draft state (after twisted stacks, before non-twisted stacks)
    if (isDraft) {
      return {
        type: 'draft',
        color: TEXT_COLORS.BANNER_DRAFT,
        text: 'Draft stack'
      };
    }

    // Non-twisted stacks
    const hasUserResponded = Boolean(meEntry && meEntry.is_answered);
    if (!hasUserResponded) {
      const isPreview = Boolean(stack?.is_preview);
      return {
        type: 'new-stack',
        color: TEXT_COLORS.BANNER_NEW,
        text: isPreview ? 'New stack! (needs selection)' : 'New stack!'
      };
    }
    if (hasAnyUnviewed) {
      return {
        type: 'new-responses',
        color: TEXT_COLORS.BANNER_RESPONSES,
        text: `New response${unviewedUsernames.length > 1 ? 's' : ''} from ${unviewedUsernames.join(', ')}`
      };
    }
    if (hasUnviewedComments) {
      return {
        type: 'new-comments',
        color: TEXT_COLORS.BANNER_COMMENTS,
        text: 'New comments!'
      };
    }
    return null;
  }, []);

  // Helper method to check if a stack is "new" (has "New stack!" banner)
  const isNewStack = useCallback((stack, meEntry) => {
    const bannerInfo = getBannerInfo(stack, meEntry);
    return bannerInfo && (bannerInfo.type === 'new-stack' || bannerInfo.type === 'new-twisted');
  }, [getBannerInfo]);

  // Helper method to check if a stack has notifications (comments or responses)
  const hasNotifications = useCallback((stack, meEntry) => {
    const bannerInfo = getBannerInfo(stack, meEntry);
    const hasNotifs = bannerInfo && (bannerInfo.type === 'new-comments' || bannerInfo.type === 'new-responses');
    console.log(`hasNotifications for stack ${stack.id}:`, { bannerInfo, hasNotifs });
    return hasNotifs;
  }, [getBannerInfo]);

  // Helper method to render banner div
  const renderBanner = useCallback((bannerInfo) => {
    if (!bannerInfo) return null;
    return (
      <div style={{ color: bannerInfo.color, fontSize: '14px', fontWeight: 'bold', marginTop: '4px' }}>
        {bannerInfo.text}
      </div>
    );
  }, []);

  const [allStacks, setAllStacks] = useState([]);
  
  // Helper function to deduplicate stacks by ID
  const deduplicateStacks = useCallback((stacks) => {
    const seen = new Set();
    return stacks.filter(stack => {
      if (seen.has(stack.id)) {
        return false;
      }
      seen.add(stack.id);
      return true;
    });
  }, []);
  
  // Initialize filter states from localStorage
  const [updatesOnly, setUpdatesOnly] = useState(() => {
    const savedFilters = getStackFilters();
    return savedFilters.updatesOnly;
  });
  const [friendsOnly, setFriendsOnly] = useState(() => {
    const savedFilters = getStackFilters();
    return savedFilters.friendsOnly;
  });
  const [noBots, setNoBots] = useState(() => {
    const savedFilters = getStackFilters();
    return savedFilters.noBots;
  });
  
  // Ref to prevent duplicate API calls
  const isLoadingRef = useRef(false);
  const [hasMoreStacks, setHasMoreStacks] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Helper method to generate contextual empty state message
  const getEmptyStateMessage = useCallback(() => {
    if (updatesOnly) {
      return (
        <>
          No new stacks. Toggle off <strong>New</strong> to see other updates, or{' '}
          <button type="button" style={helperLinkStyle} onClick={() => navigateToTab('groups')}>
            join a new group
          </button>{' '}
          for more stacks.
        </>
      );
    }
    
    if (friendsOnly) {
      return (
        <>
          No friend stacks yet. Toggle off <strong>Friends</strong> to see all stacks, or{' '}
          <button type="button" style={helperLinkStyle} onClick={() => navigateToTab('users')}>
            make some friends
          </button>{' '}
          to get started.
        </>
      );
    }
    
    if (noBots) {
      return (
        <>
          No non-bot stacks yet. Toggle off <strong>No Bots</strong> to see all stacks, or{' '}
          <button type="button" style={helperLinkStyle} onClick={() => navigateToTab('groups')}>
            join a group
          </button>{' '}
          to get started.
        </>
      );
    }
    
    // Default message when no filters are active
    return (
      <>
        No stacks yet.{' '}
        <button type="button" style={helperLinkStyle} onClick={() => navigateToTab('groups')}>
          Join a group
        </button>{' '}
        to get started.
      </>
    );
  }, [updatesOnly, friendsOnly, noBots]);

  // Handler functions for filter toggles that save to localStorage
  const handleUpdatesOnlyChange = useCallback((checked) => {
    setUpdatesOnly(checked);
    updateStackFilter('updatesOnly', checked);
  }, []);

  const handleFriendsOnlyChange = useCallback((checked) => {
    setFriendsOnly(checked);
    updateStackFilter('friendsOnly', checked);
  }, []);

  const handleNoBotsChange = useCallback((checked) => {
    setNoBots(checked);
    updateStackFilter('noBots', checked);
  }, []);

  const filteredStacks = useMemo(() => {
    try {
      // The v2 API already handles filtering, so we just return the stacks
      // The filtering is now done server-side via API parameters
      return Array.isArray(allStacks) ? allStacks : [];
    } catch (_) {
      return [];
    }
  }, [allStacks]);

  // Bulk action handlers
  const handleHideNewStacks = useCallback(async () => {
    const newStacks = filteredStacks.filter(stack => {
      const currentUserResponse = stack.me;
      return isNewStack(stack, currentUserResponse);
    });

    if (newStacks.length === 0) {
      await acknowledgeAction(
        'No New Stacks',
        'There are no new stacks to hide.'
      );
      return;
    }

    const confirmed = await confirmAction(
      'Hide New Stacks?',
      `This will hide all stacks with the <span style="color: ${TEXT_COLORS.BANNER_NEW}; font-weight: bold;">"New stack!"</span> label. You will not be able to see or answer any of these stacks.`,
      {
        confirmText: 'Hide All',
        cancelText: 'Cancel',
        confirmButtonColor: BUTTON_COLORS.USER_BULK_ACTIONS,
        cancelButtonColor: BUTTON_COLORS.CANCEL
      }
    );

    if (confirmed) {
      try {
        // Hide each new stack
        const hidePromises = newStacks.map(stack => {
          const stackId = stack.twist ? stack.twist.id : stack.id;
          return stack.twist ? 
            twistedStackAPI.hideTwistedStack(stackId) : 
            stackAPI.hideStack(stackId);
        });
        
        await Promise.all(hidePromises);
        
        // Reload the stacks to reflect changes
        const refreshed = await v2StackAPI.getStacks(0, 33, updatesOnly, friendsOnly, noBots);
        const stacksData = refreshed.data || [];
        setAllStacks(deduplicateStacks(stacksData));
        setHasMoreStacks(stacksData.length === 33);
      } catch (err) {
        console.error('Failed to hide new stacks:', err);
        await acknowledgeAction(
          'Error',
          'Failed to hide some stacks. Please try again.'
        );
      }
    }
  }, [filteredStacks, isNewStack, updatesOnly, friendsOnly, noBots, deduplicateStacks]);

  const handleClearUpdates = useCallback(async () => {
    console.log('Clear Updates clicked, checking stacks...');
    console.log('Total filtered stacks:', filteredStacks.length);
    
    const stacksWithNotifications = filteredStacks.filter(stack => {
      const currentUserResponse = stack.me;
      const hasNotifs = hasNotifications(stack, currentUserResponse);
      console.log(`Stack ${stack.id}: hasNotifications=${hasNotifs}, meEntry=`, currentUserResponse);
      return hasNotifs;
    });

    console.log('Stacks with notifications:', stacksWithNotifications.length);

    if (stacksWithNotifications.length === 0) {
      await acknowledgeAction(
        'No Updates to Clear',
        'There are no stack notifications to clear.'
      );
      return;
    }

    const confirmed = await confirmAction(
      'Clear Updates?',
      `This will acknowledge all stack notifications such as <span style="color: ${TEXT_COLORS.BANNER_COMMENTS}; font-weight: bold;">"New comments!"</span> and <span style="color: ${TEXT_COLORS.BANNER_RESPONSES}; font-weight: bold;">"new response…"</span>. You can still access those comments and responses by opening the stacks.`,
      {
        confirmText: 'Clear All',
        cancelText: 'Cancel',
        confirmButtonColor: BUTTON_COLORS.USER_BULK_ACTIONS,
        cancelButtonColor: BUTTON_COLORS.CANCEL
      }
    );

    if (confirmed) {
      try {
        console.log('User confirmed, making API calls...');
        // Mark each stack with notifications as viewed
        const viewPromises = stacksWithNotifications.map(async (stack) => {
          const stackId = stack.twist ? stack.twist.id : stack.id;
          console.log(`Marking stack ${stackId} as viewed (twisted: ${!!stack.twist})`);
          try {
            const result = stack.twist ? 
              await twistedStackAPI.markTwistedStackViewed(stackId) : 
              await stackAPI.markStackViewed(stackId);
            console.log(`Successfully marked stack ${stackId} as viewed`);
            return result;
          } catch (err) {
            console.error(`Failed to mark stack ${stackId} as viewed:`, err);
            throw err;
          }
        });
        
        console.log('Waiting for all API calls to complete...');
        await Promise.all(viewPromises);
        console.log('All API calls completed successfully');
        
        // Reload the stacks to reflect changes
        console.log('Reloading stacks to reflect changes...');
        try {
          const refreshed = await v2StackAPI.getStacks(0, 33, updatesOnly, friendsOnly, noBots);
          const stacksData = refreshed.data || [];
          setAllStacks(deduplicateStacks(stacksData));
          setHasMoreStacks(stacksData.length === 33);
          console.log('Stacks reloaded successfully');
        } catch (err) {
          console.error('Failed to reload stacks:', err);
          // Don't throw here, the main operation succeeded
        }
      } catch (err) {
        console.error('Failed to clear updates:', err);
        console.error('Error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          stack: err.stack
        });
        await acknowledgeAction(
          'Error',
          `Failed to clear some updates: ${err.message || 'Unknown error'}. Please try again.`
        );
      }
    }
  }, [filteredStacks, hasNotifications, updatesOnly, friendsOnly, noBots, deduplicateStacks]);

  useEffect(() => {
    // Prevent duplicate calls when already loading
    if (isLoadingRef.current) {
      return;
    }
    
    (async () => {
      try {
        isLoadingRef.current = true;
        setLoading(true);
        setHasMoreStacks(true);
        
        const [stacksResp, maybeCurrentUserResp, friendsResp] = currentUserProp
          ? await Promise.all([
              v2StackAPI.getStacks(0, 33, updatesOnly, friendsOnly, noBots) || Promise.resolve({ data: [] }),
              Promise.resolve({ data: currentUserProp }),
              userAPI.getMyFriends().catch(() => ({ data: [] })),
            ])
          : await Promise.all([
              v2StackAPI.getStacks(0, 33, updatesOnly, friendsOnly, noBots) || Promise.resolve({ data: [] }),
              userAPI.getMe().catch(() => ({ data: null })),
              userAPI.getMyFriends().catch(() => ({ data: [] })),
            ]);

        const stacksData = stacksResp.data || [];
        setAllStacks(deduplicateStacks(stacksData));
        setHasMoreStacks(stacksData.length === 33);
        setCurrentUser((maybeCurrentUserResp && maybeCurrentUserResp.data) || currentUserProp || null);
        setFriends(friendsResp.data || []);
      } catch (err) {
        setError('Failed to load stacks prerequisites');
        console.error(err);
      } finally {
        isLoadingRef.current = false;
        setLoading(false);
      }
    })();
  }, [currentUserProp, updatesOnly, friendsOnly, noBots, deduplicateStacks]);

  // Add refresh functionality for external triggers
  const refreshStacks = useCallback(async () => {
    try {
      setLoading(true);
      setHasMoreStacks(true);
      
      const refreshed = await v2StackAPI.getStacks(0, 33, updatesOnly, friendsOnly, noBots);
      const stacksData = refreshed.data || [];
      setAllStacks(deduplicateStacks(stacksData));
      setHasMoreStacks(stacksData.length === 33);
    } catch (err) {
      console.error('Failed to refresh stacks:', err);
    } finally {
      setLoading(false);
    }
  }, [updatesOnly, friendsOnly, noBots, deduplicateStacks]);

  // Listen for refresh events
  useEffect(() => {
    const handleRefreshStacks = () => {
      refreshStacks();
    };
    
    window.addEventListener('refreshStacks', handleRefreshStacks);
    return () => window.removeEventListener('refreshStacks', handleRefreshStacks);
  }, [refreshStacks]);

  const loadMoreStacks = useCallback(async () => {
    if (loadingMore || !hasMoreStacks) return;
    
    try {
      setLoadingMore(true);
      const nextOffset = allStacks.length;
      const response = await v2StackAPI.getStacks(nextOffset, 33, updatesOnly, friendsOnly, noBots);
      const newStacks = response.data || [];
      
      if (newStacks.length > 0) {
        setAllStacks(prev => {
          // Combine existing and new stacks, then deduplicate
          const combinedStacks = [...prev, ...newStacks];
          return deduplicateStacks(combinedStacks);
        });

        setHasMoreStacks(newStacks.length === 33);
      } else {
        setHasMoreStacks(false);
      }
    } catch (err) {
      console.error('Failed to load more stacks:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMoreStacks, allStacks.length, updatesOnly, friendsOnly, noBots, deduplicateStacks]);


  // Infinite scroll handler
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const threshold = 100; // pixels from bottom
    
    if (scrollHeight - scrollTop - clientHeight < threshold) {
      // Load more stacks if available
      if (!loadingMore && hasMoreStacks) {
        loadMoreStacks();
      }
    }
  }, [loadingMore, hasMoreStacks, loadMoreStacks]);

  // Add scroll event listener to the groups-grid element
  useEffect(() => {
    const scrollContainer = document.querySelector('.groups-grid');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handleCreateStackSuccess = useCallback(async (stackData) => {
    if (onStackClick) onStackClick({ id: stackData.id, type: 'stack' });
    try {
      // Reset pagination and reload first page
      setHasMoreStacks(true);
      const refreshed = await v2StackAPI.getStacks(0, 33, updatesOnly, friendsOnly, noBots);
      const stacksData = refreshed.data || [];
      setAllStacks(deduplicateStacks(stacksData));
      setHasMoreStacks(stacksData.length === 33);
    } catch (_) {}
  }, [onStackClick, updatesOnly, friendsOnly, noBots, deduplicateStacks]);



  // Always render container so Joyride targets (e.g., [data-tour="my-stacks"]) exist even while loading



  const navigateToTab = (tab) => {
    try {
      window.dispatchEvent(new CustomEvent('navigateTab', { detail: { tab } }));
    } catch (_) {}
  };

  const hasFriends = Array.isArray(friends) && friends.length > 0;


  const content = (
    <>
      {error && <div className="error-message">{error}</div>}
      <div style={{ marginBottom: '20px' }}>
        <h2>Stacks</h2>
        
        {/* Actions Section */}
        <div style={{ marginTop: '10px' }}>
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              padding: '8px 0',
              borderBottom: '1px solid var(--color-border)',
              marginBottom: actionsExpanded ? '10px' : '0'
            }}
            onClick={() => setActionsExpanded(!actionsExpanded)}
          >
            <h3 style={{ margin: 0, flex: 1 }}>Actions</h3>
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
              {actionsExpanded ? '−' : '+'}
            </span>
          </div>
          
          {actionsExpanded && (
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                className="btn"
                style={{ backgroundColor: BUTTON_COLORS.CREATE, color: TEXT_COLORS.BUTTON_WHITE }}
                onClick={() => {
                  setCreateStackDialog({
                    isOpen: true,
                    mode: 'friend',
                    targetId: null,
                    targetName: null,
                    targetAvatar: null
                  });
                }}
              >
                Add Friend Stack
              </button>
              <button
                className="btn"
                style={{ backgroundColor: BUTTON_COLORS.CREATE, color: TEXT_COLORS.BUTTON_WHITE }}
                onClick={() => {
                  setCreateStackDialog({
                    isOpen: true,
                    mode: 'group',
                    targetId: null,
                    targetName: null,
                    targetAvatar: null
                  });
                }}
              >
                Add Group Stack
              </button>
              <button
                className="btn"
                onClick={handleHideNewStacks}
                style={{ backgroundColor: BUTTON_COLORS.USER_BULK_ACTIONS, color: TEXT_COLORS.BUTTON_BLACK }}
              >
                Hide New Stacks
              </button>
              <button
                className="btn"
                onClick={handleClearUpdates}
                style={{ backgroundColor: BUTTON_COLORS.USER_BULK_ACTIONS, color: TEXT_COLORS.BUTTON_BLACK }}
              >
                Clear Updates
              </button>
            </div>
          )}
        </div>
        {friends !== null && !hasFriends && (
          <div style={{ marginTop: '10px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
            <div>
              You have no friends yet. <button type="button" data-tour="go-to-users-cta" style={helperLinkStyle} onClick={() => navigateToTab('users')}>Go to Users</button> to add friends.
            </div>
          </div>
        )}
      </div>


      <div className="groups-section">
        <h3 data-tour="my-stacks">My Stacks</h3>
        
        {/* Filters - always visible */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '18px', marginBottom: '6px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', color: 'var(--color-text)' }}>New</span>
            <label className="toggle">
              <input type="checkbox" checked={updatesOnly} onChange={(e) => handleUpdatesOnlyChange(e.target.checked)} />
              <span className="toggle-slider"></span>
            </label>
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', color: 'var(--color-text)' }}>Friends</span>
            <label className="toggle">
              <input type="checkbox" checked={friendsOnly} onChange={(e) => handleFriendsOnlyChange(e.target.checked)} />
              <span className="toggle-slider"></span>
            </label>
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', color: 'var(--color-text)' }}>No Bots</span>
            <label className="toggle">
              <input type="checkbox" checked={noBots} onChange={(e) => handleNoBotsChange(e.target.checked)} />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
        
        {loading ? (
          <div className="no-groups" style={{ color: '#666' }}>Loading stacks...</div>
        ) : (!allStacks || allStacks.length === 0) ? (
          <div className="no-groups" style={{ color: '#666' }}>{getEmptyStateMessage()}</div>
        ) : (
          <div className="groups-grid" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {filteredStacks.map((s, index) => {
              // Create a unique key that includes the index to prevent duplicates
              const reactKey = s?.twist ? `tw-${s.id}-${index}` : `st-${s.id}-${index}`;
              // Use the new 'me' field from the v2 API response
              const currentUserResponse = s.me;

              // Determine display user for friend stacks (show the other user besides the requester)
              let displayAvatarUrl = null;
              let displaySubtitle = null;
              
              // Handle bot stacks first
              if (s.bot) {
                // Use bot avatar from the assets folder (avatar_id already includes the path)
                displayAvatarUrl = `/assets/${s.bot.avatar_id}`;
                // For bot stacks, always show the bot display name in subtitle
                displaySubtitle = getBotStackDisplayName(s, currentUser);
              } else if (s.audience) {
                if (s.audience.user) {
                  // Always display the other person (not the requesting user)
                  let otherUsername = s.audience.user.username;
                  let otherAvatarId = s.audience.user.avatar_id;
                  if (currentUser && s.audience.user && currentUser.id === s.audience.user.id) {
                    // The friend target is me; show the owner instead
                    otherUsername = s.owner.username;
                    otherAvatarId = typeof s.owner.avatar_id === 'number' ? s.owner.avatar_id : 1;
                  } else {
                    // The friend target is the other person; use audience fields and try to resolve avatar
                    const otherUser = (s.users || []).find(u => u.user.id === s.audience.user.id) || (s.users || []).find(u => u.user.username === otherUsername);
                    otherAvatarId = otherUser && typeof otherUser.user.avatar_id === 'number' ? otherUser.user.avatar_id : s.audience.user.avatar_id;
                  }
                  displayAvatarUrl = getDiceBearUserAvatar(otherUsername, otherAvatarId || 1);
                  displaySubtitle = s?.name ? `Friend stack with ${otherUsername}` : `Friend stack with ${otherUsername}`;
                } else if (s.audience.group) {
                  displayAvatarUrl = getDiceBearGroupAvatar(s.audience.group.name);
                  displaySubtitle = s?.name ? `Group stack with ${s.audience.group.name}` : `Group stack with ${s.audience.group.name}`;
                }
              }

              // Revert to previous card layout: leading avatar and title/subtitle
              return (
                <div key={reactKey} className="group-card" style={{ cursor: 'pointer' }} onClick={async () => {
                  // Check for collaborative draft stacks first (most specific case)
                  if (s?.status === 'draft' && s.is_collab && s.me?.can_edit) {
                    try {
                      // Fetch full stack details with positions
                      const fullStackResponse = await stackAPI.getStack(s.id);
                      const fullStack = fullStackResponse.data;
                      
                      // Open CreateStack dialog for collaborative draft editing
                      setCreateStackDialog({
                        isOpen: true,
                        mode: s.audience?.friend ? 'friend' : 'group',
                        targetId: s.audience?.friend ? s.audience.friend.id : s.audience?.group?.id,
                        targetName: s.audience?.friend ? s.audience.friend.username : s.audience?.group?.name,
                        targetAvatar: s.audience?.friend ? getDiceBearUserAvatar(s.audience.friend.username, s.audience.friend.avatar_id) : getDiceBearGroupAvatar(s.audience?.group?.name),
                        title: 'Add to Collab Stack',
                        editingStack: fullStack,
                        isDraftEdit: false,
                        isCollabEdit: true
                      });
                    } catch (err) {
                      console.error('Failed to fetch full stack details:', err);
                      // Fallback to using the basic stack data
                      setCreateStackDialog({
                        isOpen: true,
                        mode: s.audience?.friend ? 'friend' : 'group',
                        targetId: s.audience?.friend ? s.audience.friend.id : s.audience?.group?.id,
                        targetName: s.audience?.friend ? s.audience.friend.username : s.audience?.group?.name,
                        targetAvatar: s.audience?.friend ? getDiceBearUserAvatar(s.audience.friend.username, s.audience.friend.avatar_id) : getDiceBearGroupAvatar(s.audience?.group?.name),
                        title: 'Add to Collab Stack',
                        editingStack: s,
                        isDraftEdit: false,
                        isCollabEdit: true
                      });
                    }
                  } else if (s?.status === 'draft' && !s.is_collab) {
                    // Regular draft stack (not collaborative)
                    try {
                      // Fetch full stack details with positions
                      const fullStackResponse = await stackAPI.getStack(s.id);
                      const fullStack = fullStackResponse.data;
                      
                      // Open CreateStack dialog for draft editing
                      setCreateStackDialog({
                        isOpen: true,
                        mode: s.audience?.friend ? 'friend' : 'group',
                        targetId: s.audience?.friend ? s.audience.friend.id : s.audience?.group?.id,
                        targetName: s.audience?.friend ? s.audience.friend.username : s.audience?.group?.name,
                        targetAvatar: s.audience?.friend ? getDiceBearUserAvatar(s.audience.friend.username, s.audience.friend.avatar_id) : getDiceBearGroupAvatar(s.audience?.group?.name),
                        title: 'Edit Draft Stack',
                        editingStack: fullStack,
                        isDraftEdit: true,
                        isCollabEdit: false
                      });
                    } catch (err) {
                      console.error('Failed to fetch full stack details:', err);
                      // Fallback to using the basic stack data
                      setCreateStackDialog({
                        isOpen: true,
                        mode: s.audience?.friend ? 'friend' : 'group',
                        targetId: s.audience?.friend ? s.audience.friend.id : s.audience?.group?.id,
                        targetName: s.audience?.friend ? s.audience.friend.username : s.audience?.group?.name,
                        targetAvatar: s.audience?.friend ? getDiceBearUserAvatar(s.audience.friend.username, s.audience.friend.avatar_id) : getDiceBearGroupAvatar(s.audience?.group?.name),
                        title: 'Edit Draft Stack',
                        editingStack: s,
                        isDraftEdit: true,
                        isCollabEdit: false
                      });
                    }
                  } else if (s.status === 'published' && s.me?.can_edit && s.is_collab) {
                    // Published collaborative stack
                    setCreateStackDialog({
                      isOpen: true,
                      mode: s.audience?.friend ? 'friend' : 'group',
                      targetId: s.audience?.friend ? s.audience.friend.id : s.audience?.group?.id,
                      targetName: s.audience?.friend ? s.audience.friend.username : s.audience?.group?.name,
                      targetAvatar: s.audience?.friend ? getDiceBearUserAvatar(s.audience.friend.username, s.audience.friend.avatar_id) : getDiceBearGroupAvatar(s.audience?.group?.name),
                      title: 'Add to Collab Stack',
                      editingStack: s,
                      isDraftEdit: false,
                      isCollabEdit: true
                    });
                  } else if (s.is_preview) {
                    // Preview stack - open bot stack selector
                    setBotStackSelector({ isOpen: true, stackId: s.id });
                  } else {
                    // Normal stack click
                    onStackClick && onStackClick({ id: s?.twist ? s.twist.id : s.id, type: s?.twist ? 'twisted' : 'stack' });
                  }
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ flexShrink: 0 }}>
                      {displayAvatarUrl ? (
                        <img 
                          src={displayAvatarUrl} 
                          alt={s.bot ? `${s.bot.name} avatar` : `${s.audience?.name || 'user'} avatar`} 
                          style={{ width: '40px', height: '40px', borderRadius: '50%' }} 
                        />
                      ) : (
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#666' }}>
                          ?
                        </div>
                      )}
                    </div>
                    <div className="group-info" style={{ flex: 1 }}>
                      <div className="group-name" style={{
                        ...(s?.twist ? { color: 'var(--color-purple)' } : {}),
                        ...(s?.is_collab && !s?.twist ? { color: TEXT_COLORS.BANNER_COLLAB } : {}),
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-all',
                        hyphens: 'auto',
                        lineHeight: '1.2'
                      }}>
                        {s?.twist ? (
                          <>
                            {(s?.name || getStackDisplayName(s))}
                          </>
                        ) : (
                          getStackDisplayName(s)
                        )}
                      </div>
                      <div style={{ color: '#666', fontSize: '14px' }}>
                        {s?.twist && (
                          <div style={{ fontWeight: 800, fontStyle: 'italic', color: 'var(--color-purple)' }}>{s.twist.description}</div>
                        )}
                        {s.bot ? (
                          <span>
                            {s?.twist ? (
                              <>
                                {s.bot.audience?.user ? `Friend stack with ${s.bot.audience.user.username}` : 
                                 s.bot.audience?.group ? `Group stack with ${s.bot.audience.group.name}` : 
                                 'Bot stack'}
                              </>
                            ) : (
                              displaySubtitle && displaySubtitle.includes('\n') ? (
                                <div>
                                  {displaySubtitle.split('\n').map((line, index) => (
                                    <div key={index} style={{ fontSize: index === 0 ? 'inherit' : '12px', color: index === 0 ? 'inherit' : '#666', marginTop: index === 0 ? 0 : '2px' }}>
                                      {line}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                displaySubtitle
                              )
                            )}
                          </span>
                        ) : s.audience ? (
                          <span>
                            {s?.twist ? (
                              <>
                                {(s.audience.user) ? `Friend stack with ${s.audience.user.username}` : (s.audience.group ? `Group stack with ${s.audience.group.name}` : 'Stack')}
                              </>
                            ) : (
                              displaySubtitle
                            )}
                          </span>
                        ) : (
                          <span>Created {new Date(s.created_at).toLocaleString()}</span>
                        )}
                        {s?.twist && (
                          <div style={{ marginTop: '2px' }}>Twisted by {s.owner.username}</div>
                        )}
                      </div>
                      {renderBanner(getBannerInfo(s, currentUserResponse))}
                    </div>
                  </div>
                </div>
              );
            })}
            {loadingMore && (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: 'var(--color-text-muted)' }}>
                Loading more stacks...
              </div>
            )}
            {!hasMoreStacks && allStacks.length > 0 && (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
                No more stacks to load
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );

  if (isEmbedded) {
    return (
      <>
        {content}
        <CreateStack
          isOpen={createStackDialog.isOpen}
          onClose={() => setCreateStackDialog({ isOpen: false, mode: null, targetId: null, targetName: null, targetAvatar: null, editingStack: null, isDraftEdit: false, isCollabEdit: false })}
          onSuccess={handleCreateStackSuccess}
          mode={createStackDialog.mode}
          targetId={createStackDialog.targetId}
          targetName={createStackDialog.targetName}
          targetAvatar={createStackDialog.targetAvatar}
          title={createStackDialog.title}
          editingStack={createStackDialog.editingStack}
          isDraftEdit={createStackDialog.isDraftEdit}
          isCollabEdit={createStackDialog.isCollabEdit}
          allowTargetSelection={!createStackDialog.targetId && !createStackDialog.editingStack}
        />
        <BotStackSelector
          isOpen={botStackSelector.isOpen}
          onClose={() => setBotStackSelector({ isOpen: false, stackId: null })}
          stackId={botStackSelector.stackId}
          onSelect={(stackId) => {
            setBotStackSelector({ isOpen: false, stackId: null });
            onStackClick && onStackClick({ id: stackId, type: 'stack' });
          }}
        />
      </>
    );
  }

  return (
    <div className="groups-modal">
      <div className="groups-content">
        <div className="groups-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Stacks</h2>
            <button onClick={onClose} className="close-btn">&times;</button>
          </div>
          
          {/* Actions Section */}
          <div style={{ marginTop: '10px' }}>
            <div 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                padding: '8px 0',
                borderBottom: '1px solid var(--color-border)',
                marginBottom: actionsExpanded ? '10px' : '0'
              }}
              onClick={() => setActionsExpanded(!actionsExpanded)}
            >
              <h3 style={{ margin: 0, flex: 1 }}>Actions</h3>
              <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {actionsExpanded ? '−' : '+'}
              </span>
            </div>
            
            {actionsExpanded && (
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button
                  className="btn"
                  data-tour="add-friend-stack"
                  style={{ backgroundColor: BUTTON_COLORS.CREATE, color: TEXT_COLORS.BUTTON_WHITE }}
                  onClick={() => {
                    setCreateStackDialog({
                      isOpen: true,
                      mode: 'friend',
                      targetId: null,
                      targetName: null,
                      targetAvatar: null
                    });
                  }}
                >
                  Add Friend Stack
                </button>
                <button
                  className="btn"
                  data-tour="add-group-stack"
                  style={{ backgroundColor: BUTTON_COLORS.CREATE, color: TEXT_COLORS.BUTTON_WHITE }}
                  onClick={() => {
                    setCreateStackDialog({
                      isOpen: true,
                      mode: 'group',
                      targetId: null,
                      targetName: null,
                      targetAvatar: null
                    });
                  }}
                >
                  Add Group Stack
                </button>
                <button
                  className="btn"
                  onClick={handleHideNewStacks}
                  style={{ backgroundColor: BUTTON_COLORS.USER_BULK_ACTIONS, color: TEXT_COLORS.BUTTON_BLACK }}
                >
                  Hide New Stacks
                </button>
                <button
                  className="btn"
                  onClick={handleClearUpdates}
                  style={{ backgroundColor: BUTTON_COLORS.USER_BULK_ACTIONS, color: TEXT_COLORS.BUTTON_BLACK }}
                >
                  Clear Updates
                </button>
              </div>
            )}
          </div>
          {!hasFriends && (
            <div style={{ marginTop: '10px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
              <div>
                You have no friends yet. <button type="button" style={helperLinkStyle} onClick={() => navigateToTab('users')}>Go to Users</button> to add friends.
              </div>
            </div>
          )}
        </div>
        <div className="groups-body">{content}</div>
      </div>
      <CreateStack
        isOpen={createStackDialog.isOpen}
        onClose={() => setCreateStackDialog({ isOpen: false, mode: null, targetId: null, targetName: null, targetAvatar: null, editingStack: null, isDraftEdit: false, isCollabEdit: false })}
        onSuccess={handleCreateStackSuccess}
        mode={createStackDialog.mode}
        targetId={createStackDialog.targetId}
        targetName={createStackDialog.targetName}
        targetAvatar={createStackDialog.targetAvatar}
        title={createStackDialog.title}
        editingStack={createStackDialog.editingStack}
        isDraftEdit={createStackDialog.isDraftEdit}
        isCollabEdit={createStackDialog.isCollabEdit}
        allowTargetSelection={!createStackDialog.targetId && !createStackDialog.editingStack}
      />
      <BotStackSelector
        isOpen={botStackSelector.isOpen}
        onClose={() => setBotStackSelector({ isOpen: false, stackId: null })}
        stackId={botStackSelector.stackId}
        onSelect={(stackId) => {
          setBotStackSelector({ isOpen: false, stackId: null });
          onStackClick && onStackClick({ id: stackId, type: 'stack' });
        }}
      />
    </div>
  );
};

export default Stacks;



